import React, { useState, useCallback } from "react";
import { useSuperAdminFetch } from "@/contexts/SuperAdminContext";
import { Account } from "@/contexts/SuperAdminContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, X, AlertCircle } from "lucide-react";
import { ALL_MODULES, ALWAYS_ON_MODULES, MODULE_SECTIONS, type ModuleDef } from "@/lib/modules";

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahrain","Bangladesh","Belarus","Belgium","Belize","Benin","Bolivia","Bosnia and Herzegovina",
  "Botswana","Brazil","Bulgaria","Cambodia","Cameroon","Canada","Chile","China","Colombia","Costa Rica",
  "Croatia","Cuba","Cyprus","Czech Republic","Denmark","Dominican Republic","Ecuador","Egypt","El Salvador",
  "Estonia","Ethiopia","Finland","France","Georgia","Germany","Ghana","Greece","Guatemala","Honduras",
  "Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan",
  "Jordan","Kazakhstan","Kenya","Kosovo","Kuwait","Kyrgyzstan","Latvia","Lebanon","Libya","Lithuania",
  "Luxembourg","Malaysia","Malta","Mexico","Moldova","Monaco","Montenegro","Morocco","Mozambique",
  "Myanmar","Nepal","Netherlands","New Zealand","Nicaragua","Nigeria","North Macedonia","Norway","Oman",
  "Pakistan","Panama","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia",
  "Saudi Arabia","Senegal","Serbia","Singapore","Slovakia","Slovenia","Somalia","South Africa","South Korea",
  "Spain","Sri Lanka","Sudan","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand",
  "Tunisia","Turkey","Turkmenistan","Uganda","Ukraine","United Arab Emirates","United Kingdom",
  "United States","Uruguay","Uzbekistan","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe",
];

const CURRENCIES = [
  { code: "USD", label: "USD — US Dollar" },
  { code: "EUR", label: "EUR — Euro" },
  { code: "GBP", label: "GBP — British Pound" },
  { code: "CHF", label: "CHF — Swiss Franc" },
  { code: "SEK", label: "SEK — Swedish Krona" },
  { code: "NOK", label: "NOK — Norwegian Krone" },
  { code: "DKK", label: "DKK — Danish Krone" },
  { code: "PLN", label: "PLN — Polish Zloty" },
  { code: "CZK", label: "CZK — Czech Koruna" },
  { code: "HUF", label: "HUF — Hungarian Forint" },
  { code: "RON", label: "RON — Romanian Leu" },
  { code: "BGN", label: "BGN — Bulgarian Lev" },
  { code: "HRK", label: "HRK — Croatian Kuna" },
  { code: "MKD", label: "MKD — Macedonian Denar" },
  { code: "RSD", label: "RSD — Serbian Dinar" },
  { code: "BAM", label: "BAM — Bosnian Mark" },
  { code: "ALL", label: "ALL — Albanian Lek" },
  { code: "AUD", label: "AUD — Australian Dollar" },
  { code: "CAD", label: "CAD — Canadian Dollar" },
  { code: "NZD", label: "NZD — New Zealand Dollar" },
  { code: "SGD", label: "SGD — Singapore Dollar" },
  { code: "AED", label: "AED — UAE Dirham" },
  { code: "SAR", label: "SAR — Saudi Riyal" },
  { code: "INR", label: "INR — Indian Rupee" },
  { code: "JPY", label: "JPY — Japanese Yen" },
  { code: "CNY", label: "CNY — Chinese Yuan" },
  { code: "BRL", label: "BRL — Brazilian Real" },
  { code: "MXN", label: "MXN — Mexican Peso" },
  { code: "ZAR", label: "ZAR — South African Rand" },
  { code: "TRY", label: "TRY — Turkish Lira" },
  { code: "UAH", label: "UAH — Ukrainian Hryvnia" },
  { code: "KZT", label: "KZT — Kazakhstani Tenge" },
];

interface AccountFormProps {
  open: boolean;
  account?: Account;
  onClose: () => void;
  onSaved: () => void;
}

const SLUG_RE = /^[a-z][a-z0-9-]{1,61}[a-z0-9]$/;

export default function AccountForm({ account, onClose, onSaved }: AccountFormProps) {
  const apiFetch = useSuperAdminFetch();
  const isEdit = !!account;

  const [name, setName] = useState(account?.name || "");
  const [slug, setSlug] = useState(account?.slug || "");
  const [contactEmail, setContactEmail] = useState(account?.contact_email || "");
  const [maxUsers, setMaxUsers] = useState(account?.max_users?.toString() || "");
  const [notes, setNotes] = useState(account?.notes || "");
  const [primaryColor, setPrimaryColor] = useState(account?.primary_color || "#7c3aed");
  const [status, setStatus] = useState<string>(account?.status || "active");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [country, setCountry] = useState(account ? "" : "");
  const [currency, setCurrency] = useState(account ? "" : "EUR");

  const [enabledModules, setEnabledModules] = useState<Record<string, boolean>>(() => {
    const existing = account?.enabled_modules as Record<string, boolean> | null | undefined;
    if (existing) return { ...existing };
    // Default: all enabled
    const defaults: Record<string, boolean> = {};
    ALL_MODULES.forEach((m) => { defaults[m.key] = true; });
    return defaults;
  });

  function handleNameChange(val: string) {
    setName(val);
    if (!isEdit) {
      const auto = val
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/--+/g, "-")
        .replace(/^-+|-+$/g, "");
      setSlug(auto);
    }
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");

      if (!name.trim()) {
        setError("Company name is required");
        return;
      }
      if (!isEdit && !SLUG_RE.test(slug)) {
        setError(
          "Subdomain must be 3–63 chars, start with a letter, and contain only a-z, 0-9, or hyphens"
        );
        return;
      }

      setSaving(true);
      try {
        if (isEdit) {
          await apiFetch(`/api/superadmin/accounts/${account!.id}`, {
            method: "PATCH",
            body: JSON.stringify({
              name: name.trim(),
              status,
              contactEmail: contactEmail || null,
              maxUsers: maxUsers ? parseInt(maxUsers) : null,
              notes: notes || null,
              primaryColor: primaryColor || null,
              enabledModules,
            }),
          });
        } else {
          await apiFetch("/api/superadmin/accounts", {
            method: "POST",
            body: JSON.stringify({
              name: name.trim(),
              slug,
              contactEmail: contactEmail || null,
              maxUsers: maxUsers ? parseInt(maxUsers) : null,
              notes: notes || null,
              primaryColor: primaryColor || null,
              enabledModules,
              country: country || null,
              currency: currency || "EUR",
            }),
          });
        }
        onSaved();
        onClose();
      } catch (err: any) {
        setError(err.message || "Failed to save account");
      } finally {
        setSaving(false);
      }
    },
    [name, slug, status, contactEmail, maxUsers, notes, primaryColor, enabledModules, country, currency, account, isEdit, apiFetch, onSaved, onClose]
  );

  return (
    <div className="w-full bg-[#161b22] border border-white/[0.08] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
      {/* Header */}
      <div className="flex items-center justify-between px-8 pt-7 pb-5 border-b border-white/[0.06] flex-shrink-0">
        <h2 className="text-lg font-bold text-[#e6edf3]">
          {isEdit ? "Edit Account" : "Create New Account"}
        </h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8 text-white/35 hover:text-white/80 hover:bg-white/8"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Form — two-panel layout: account info left, modules right */}
      <form onSubmit={handleSubmit} className="flex flex-1 overflow-hidden">
        {/* LEFT PANEL — Account Info */}
        <div className="w-[380px] flex-shrink-0 px-8 py-6 space-y-5 border-r border-white/[0.06] overflow-y-auto">
          {/* Company Name */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Company Name <span className="text-red-400">*</span>
            </Label>
            <Input
              value={name}
              onChange={e => handleNameChange(e.target.value)}
              required
              placeholder="e.g. Acme Corp"
              className="bg-white/[0.05] border-white/10 text-[#e6edf3] placeholder:text-white/20 focus:border-purple-500/60 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <p className="text-[11px] text-white/30 leading-relaxed">
              Shown in the tenant's Organization module and used in welcome emails.
            </p>
          </div>

          {/* Slug */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Subdomain / Slug {!isEdit && <span className="text-red-400">*</span>}
            </Label>
            <div className="relative">
              <Input
                value={slug}
                onChange={e => !isEdit && setSlug(e.target.value.toLowerCase())}
                required={!isEdit}
                disabled={isEdit}
                placeholder="acme-corp"
                title={isEdit ? "Subdomain cannot be changed after creation" : undefined}
                className="bg-white/[0.05] border-white/10 text-[#e6edf3] placeholder:text-white/20 focus:border-purple-500/60 focus-visible:ring-0 focus-visible:ring-offset-0 pr-28 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/25 pointer-events-none select-none">
                .portal.besthr.app
              </span>
            </div>
            {!isEdit && slug && (
              <p className="text-xs text-white/35 mt-1">
                This will be the subdomain:{" "}
                <span className="text-purple-400 font-mono">{slug}.portal.besthr.app</span>
              </p>
            )}
            {isEdit && (
              <p className="text-xs text-white/30 mt-1">Subdomain cannot be changed after creation.</p>
            )}
          </div>

          {/* Status (edit only) */}
          {isEdit && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                Status
              </Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-white/[0.05] border-white/10 text-[#e6edf3] focus:ring-0 focus:ring-offset-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1c2430] border-white/10 text-[#e6edf3]">
                  <SelectItem value="active" className="focus:bg-white/10">Active</SelectItem>
                  <SelectItem value="suspended" className="focus:bg-white/10">Suspended</SelectItem>
                  <SelectItem value="pending" className="focus:bg-white/10">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Contact Email */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Contact Email
            </Label>
            <Input
              type="email"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              placeholder="it@company.com"
              className="bg-white/[0.05] border-white/10 text-[#e6edf3] placeholder:text-white/20 focus:border-purple-500/60 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            {!isEdit && (
              <p className="text-[11px] text-white/30 leading-relaxed">
                A "Welcome to BestHR" email will be sent here when the account is created.
              </p>
            )}
          </div>

          {/* Country + Currency (create only) */}
          {!isEdit && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                  Country
                </Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="bg-white/[0.05] border-white/10 text-[#e6edf3] focus:ring-0 focus:ring-offset-0">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1c2430] border-white/10 text-[#e6edf3] max-h-60">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c} className="focus:bg-white/10 text-xs">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                  Currency
                </Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="bg-white/[0.05] border-white/10 text-[#e6edf3] focus:ring-0 focus:ring-offset-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1c2430] border-white/10 text-[#e6edf3] max-h-60">
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code} className="focus:bg-white/10 text-xs">{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Max Users + Primary Color row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                Max Users
              </Label>
              <Input
                type="number"
                min="1"
                value={maxUsers}
                onChange={e => setMaxUsers(e.target.value)}
                placeholder="Unlimited"
                className="bg-white/[0.05] border-white/10 text-[#e6edf3] placeholder:text-white/20 focus:border-purple-500/60 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                Primary Color
              </Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={e => setPrimaryColor(e.target.value)}
                  className="h-9 w-12 rounded-md border border-white/10 bg-white/[0.05] cursor-pointer p-0.5"
                />
                <Input
                  value={primaryColor}
                  onChange={e => setPrimaryColor(e.target.value)}
                  placeholder="#7c3aed"
                  className="bg-white/[0.05] border-white/10 text-[#e6edf3] placeholder:text-white/20 focus:border-purple-500/60 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-sm"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Notes
            </Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Internal notes about this account…"
              className="bg-white/[0.05] border-white/10 text-[#e6edf3] placeholder:text-white/20 focus:border-purple-500/60 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none min-h-[56px]"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-red-500/10 border border-red-500/25 text-red-300 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* Footer buttons */}
          <div className="flex items-center justify-end gap-2.5 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-white/10 bg-white/[0.04] text-white/70 hover:text-white hover:bg-white/8 hover:border-white/15"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold shadow-lg shadow-purple-700/30 disabled:opacity-60 gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Account"}
            </Button>
          </div>
        </div>

        {/* RIGHT PANEL — Modules */}
        <div className="flex-1 min-w-0 px-6 py-6 flex flex-col overflow-hidden">
          <div className="flex items-center gap-3 mb-4 flex-shrink-0">
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Enabled Modules
            </Label>
            <span className="text-[10px] text-white/30">
              {ALL_MODULES.filter((m) => !m.alwaysOn && enabledModules[m.key] !== false).length} / {ALL_MODULES.filter((m) => !m.alwaysOn).length} enabled
            </span>
            <div className="flex gap-2 ml-auto">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const next = { ...enabledModules };
                  ALL_MODULES.forEach((m) => { next[m.key] = true; });
                  setEnabledModules(next);
                }}
                className="h-6 text-[10px] px-2 border-white/10 bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/8"
              >
                Enable All
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const next = { ...enabledModules };
                  ALL_MODULES.forEach((m) => {
                    next[m.key] = m.alwaysOn ? true : false;
                  });
                  setEnabledModules(next);
                }}
                className="h-6 text-[10px] px-2 border-white/10 bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/8"
              >
                Disable All
              </Button>
            </div>
          </div>
          <div className="overflow-y-auto flex-1 pr-1">
          <div className="grid grid-cols-3 gap-x-6 gap-y-5">
            {(Object.keys(MODULE_SECTIONS) as Array<ModuleDef["section"]>).map((section) => {
              const items = ALL_MODULES.filter((m) => m.section === section);
              if (items.length === 0) return null;
              return (
                <div key={section} className="min-w-0">
                  <p className="text-[10px] font-semibold text-white/35 uppercase tracking-widest mb-2 pb-1.5 border-b border-white/[0.06]">
                    {MODULE_SECTIONS[section]}
                  </p>
                  <div className="space-y-0.5">
                    {items.map((m) => (
                      <label
                        key={m.key}
                        className={`flex items-center gap-1.5 py-0.5 px-1.5 rounded text-xs ${
                          m.alwaysOn ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-white/[0.04]"
                        }`}
                      >
                        <Switch
                          checked={m.alwaysOn ? true : enabledModules[m.key] !== false}
                          disabled={m.alwaysOn}
                          onCheckedChange={(checked) => {
                            if (m.alwaysOn) return;
                            setEnabledModules((prev) => ({ ...prev, [m.key]: checked }));
                          }}
                          className="scale-[0.6] flex-shrink-0"
                        />
                        <span className="text-[11px] text-white/70 truncate">{m.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        </div>
      </form>
    </div>
  );
}
